import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { formatPhone } from '../lib/format';
import { Button, Chip, EmptyState, ErrorState, Loading } from './ui';
import './DayList.css';

/**
 * One day's appointments. Used by the staff home and by the cart's own day, so
 * both read from the same place and can never disagree (§0.5, one shared layer).
 *
 * Staff screens show SHOP time, with the guest's own time as a sapphire tag (§5).
 */

interface Booking {
  id: string;
  start_at: string;
  duration_min: number;
  wants_to_see: string | null;
  status: 'tentative' | 'held';
  cart_id: string;
  cart_name: string;
  customer_id: string;
  customer_name: string;
  customer_phone: string;
  customer_timezone: string | null;
}

/** A booked call can be started from 10 minutes before its time (§6). */
const EARLY_START_MIN = 10;

export function DayList({
  cartId,
  emptyLine,
  showCartName = true,
  onStart,
  startingId,
}: {
  cartId?: string;
  emptyLine: string;
  showCartName?: boolean;
  /** Cart only. Phones never get this — they cannot start calls (§3). */
  onStart?: (bookingId: string) => void | Promise<void>;
  startingId?: string | null;
}) {
  const [rows, setRows] = useState<Booking[] | null>(null);
  const [shopTz, setShopTz] = useState('Asia/Kolkata');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const qs = cartId ? `?cart_id=${encodeURIComponent(cartId)}` : '';
      const res = await api<{ bookings: Booking[]; shop_tz: string }>(`/api/bookings/day${qs}`);
      setRows(res.bookings);
      setShopTz(res.shop_tz);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the day.');
    }
  }, [cartId]);

  useEffect(() => {
    void load();
  }, [load]);

  // No stale screens (§0.5).
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={() => void load()} />;
  if (!rows) return <Loading label="Loading the day…" />;
  if (rows.length === 0) return <EmptyState line={emptyLine} />;

  const canStart = (iso: string) =>
    Date.now() >= new Date(iso).getTime() - EARLY_START_MIN * 60_000;

  const at = (iso: string, tz: string) =>
    new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false })
      .format(new Date(iso));

  return (
    <ul className="day">
      {rows.map((b) => (
        <li key={b.id} className="day__row">
          <span className="day__time">{at(b.start_at, shopTz)}</span>

          <div className="day__who">
            <Link to={`/customers/${b.customer_id}`} className="day__name">
              {b.customer_name}
            </Link>
            <span className="day__phone">{formatPhone(b.customer_phone)}</span>
          </div>

          <div className="day__tags">
            {b.status === 'held' ? (
              <Chip tone="live">Held</Chip>
            ) : (
              <Chip tone="gold">Not confirmed</Chip>
            )}
            {showCartName ? <Chip tone="neutral">{b.cart_name}</Chip> : null}
            {b.customer_timezone ? (
              <Chip tone="info">{at(b.start_at, b.customer_timezone)} their time</Chip>
            ) : null}
          </div>

          {b.wants_to_see ? <p className="day__see">{b.wants_to_see}</p> : null}

          {onStart ? (
            <div className="day__start">
              {canStart(b.start_at) ? (
                <Button onClick={() => void onStart(b.id)} busy={startingId === b.id}>
                  Start Mulaqaat
                </Button>
              ) : (
                <span className="day__soon">Opens {EARLY_START_MIN} minutes before</span>
              )}
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
